/**
 * useSettingsSectionAccess — które sekcje ekranu Ustawień widzi zalogowany użytkownik tenanta.
 *
 * Jedno źródło bramek dla DWÓCH miejsc: nawigacji Ustawień (`pages/Settings.tsx`) i globalnej
 * wyszukiwarki (Ctrl+K, `components/tenant/GlobalSearch.tsx`) — żeby wyszukiwarka nie
 * podsuwała sekcji, których user w Ustawieniach nie zobaczy. Backend egzekwuje uprawnienia
 * niezależnie; to tylko widoczność w UI.
 */

import { type TenantMeResponse, useTenantAuth } from '@/contexts/TenantAuthContext';
import { canManageNonWorkingDays } from '@/hooks/useCanManageNonWorkingDays';
import { canManageProcessDefinitions } from '@/hooks/useCanManageProcessDefinitions';
import { canManageProcessProcedures } from '@/hooks/useCanManageProcessProcedures';
import { canManageRecordProcedures } from '@/hooks/useCanManageRecordProcedures';

export interface SettingsSectionAccess {
  isSuperUser: boolean;
  canManageCustomModules: boolean;
  /** F22 (widoki dodatkowe, ADR-0087 Decyzja 2) — jedno uprawnienie tenant-wide. */
  canManageAdditionalViews: boolean;
  /** F23 T1.7 — kreator widgetów danych (encja tenant-globalna). */
  canManageWidgets: boolean;
  /** F23 Etap 2 — domyślny pulpit tenanta (singleton). */
  canManageDefaultDashboard: boolean;
  /** Oś „Kategoria" — to samo uprawnienie, które backend egzekwuje na /record-categories. */
  canManageRecordCategories: boolean;
  canManageProcessDefinitions: boolean;
  canManageProcessProcedures: boolean;
  canManageRecordProcedures: boolean;
  /** Sekcja „Procedury SQL" — widoczna, gdy user zarządza którymkolwiek z jej katalogów (zakładek). */
  canManageSqlProcedures: boolean;
  /** Kalendarz dni wolnych od pracy — jedno uprawnienie tenant-wide. */
  canManageNonWorkingDays: boolean;
  /** Akcje list Faza 2 — dziś super-user; docelowe uprawnienie dojdzie z backendem. */
  canManageListActions: boolean;
}

/** Czysta wersja — liczy dostęp z danych sesji (`null` = brak sesji tenanta → nic). */
export function settingsSectionAccessFor(
  me: TenantMeResponse | null | undefined,
): SettingsSectionAccess {
  const isSuperUser = me?.user.isSuperUser === true;
  const has = (code: string) => isSuperUser || (me?.permissions.includes(code) ?? false);
  const manageProcessProcedures = canManageProcessProcedures(me);
  const manageRecordProcedures = canManageRecordProcedures(me);
  return {
    isSuperUser,
    canManageCustomModules: has('crm_core.custom_modules.manage'),
    canManageAdditionalViews: has('crm_core.additional_views.manage'),
    canManageWidgets: has('crm_core.widgets.manage'),
    canManageDefaultDashboard: has('crm_core.dashboard.manage_default'),
    canManageRecordCategories: has('crm_core.system_modules.manage'),
    canManageProcessDefinitions: canManageProcessDefinitions(me),
    canManageProcessProcedures: manageProcessProcedures,
    canManageRecordProcedures: manageRecordProcedures,
    canManageSqlProcedures: manageProcessProcedures || manageRecordProcedures,
    canManageNonWorkingDays: canManageNonWorkingDays(me),
    canManageListActions: has('crm_core.list_actions.manage'),
  };
}

/** Bramki sekcji + informacja, czy sesja tenanta jest już wczytana (jedno źródło: `useTenantAuth`). */
export interface SettingsSectionAccessState extends SettingsSectionAccess {
  /**
   * `true`, gdy sesja tenanta (`me`) jest wczytana. Dopóki jest `false`, WSZYSTKIE flagi dostępu są
   * `false` (brak danych o uprawnieniach), więc ekran nie może jeszcze uznać sekcji za niedostępną.
   * Uwaga: kontekst auth zamienia każdy błąd `/auth/me` na `null` (401 obsługuje przekierowanie
   * w interceptorze), więc po innym błędzie flaga zostaje `false` na stałe.
   */
  sessionLoaded: boolean;
}

export function useSettingsSectionAccess(): SettingsSectionAccessState {
  const { me } = useTenantAuth();
  return { ...settingsSectionAccessFor(me), sessionLoaded: me !== null };
}
